import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { Loading } from '../../../common/loading'
import { Notification } from '../../../common/notification'
import { PanelContainer } from '../../../common/panel/PanelContainer'
import { SelectCurrencyUnit } from '../../../common/selectCurrencyUnit'
import { SCREEN } from '../../../common/selectCurrencyUnit/constant'
import { selectAllowSetDefaultUnit } from '../../../common/selectCurrencyUnit/slice'
import { COMPONENTS } from '../../../routers/constants'
import { COMPANY_TYPES } from '../../../utils/common'
import { CompanyInfo } from '../../common/header/companyInfo'
import useCheckIsCompanyType from '../../common/header/companyInfo/useCheckIsCompanyType'
import { Filter } from '../../common/header/filterBar'
import { resetFullStoreDetailTemplate } from '../../common/preferenceSetting/detailTemplate/store/slice'
import { Layout } from '../../Layout'
import FiscalYear from './FiscalYear'
import { resetAllStore } from './helper'
import Highlight from './highlight'
import './i18n'
import Panel from './Panel'
import PreferenceSettingFinancialAnalysis from './PreferenceSettingFinancialAnalysis'
import PreferenceSettingHeader from './PreferenceSettingHeader'
import {
  changePanelId,
  keyLoading,
  selectLoading,
  selectPanelId,
} from './store/slice'
import useCallApi from './useCallApi'

export const FinancialAnalysis = () => {
  useCallApi()

  const isUnavailable = useCheckIsCompanyType([
    COMPANY_TYPES.FUNDS,
    COMPANY_TYPES.INSURANCE,
  ])

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const panelId = useSelector(selectPanelId)
  const loading = useSelector(
    selectLoading(keyLoading.DEFAULT_TEMPLATE_FOR_LAYOUT),
  )
  const allowSetDefaultCurrency = useSelector(
    selectAllowSetDefaultUnit(SCREEN.FINANCIAL_ANALYSIS),
  )

  const { organizationId, preferenceSetting } = new Proxy(
    new URLSearchParams(location.search),
    {
      get: (searchParam, prop) => searchParam.get(prop),
    },
  )

  const setIsShowPreferenceSetting = (payload) => {
    if (payload) {
      dispatch(resetFullStoreDetailTemplate())
    }
    const params = `?organizationId=${organizationId}${
      payload ? '&preferenceSetting=true' : ''
    }&showDetail=true`
    navigate(
      COMPONENTS.companyAnalysis.components.financial.components
        .financialAnalysis.route + params,
    )
  }

  useEffect(() => {
    return () => {
      resetAllStore(dispatch)
    }
  }, [])

  const renderTopComponent = () => {
    return !preferenceSetting && <CompanyInfo />
  }

  const renderBottomComponent = () => {
    if (isUnavailable) {
      return (
        <Notification
          isI18n
          value="errorCommon.COMPANY_TYPE_ERROR"
          style={{ fontSize: 20 }}
        />
      )
    }

    if (loading || allowSetDefaultCurrency) {
      return <Loading />
    }

    return preferenceSetting ? (
      <PreferenceSettingFinancialAnalysis
        setIsShowPreferenceSetting={setIsShowPreferenceSetting}
      />
    ) : (
      <>
        <Highlight setIsShowPreferenceSetting={setIsShowPreferenceSetting} />
        <PanelContainer
          key={panelId.length}
          changeLayout={(data) => {
            dispatch(changePanelId(data.map((panel) => panel.id)))
          }}
          isAllowDragDrop
          handleAddNewPanel={() => setIsShowPreferenceSetting(true)}
        >
          {panelId.map((id) => (
            <Panel key={id} id={id} />
          ))}
        </PanelContainer>
      </>
    )
  }

  const renderStickyComponent = () => {
    if (isUnavailable || allowSetDefaultCurrency) {
      return
    }

    return (
      <Filter
        renderLeft={() => (
          <PreferenceSettingHeader
            setIsShowPreferenceSetting={setIsShowPreferenceSetting}
          />
        )}
        renderRight={() => (
          <>
            <FiscalYear />
            <SelectCurrencyUnit screen={SCREEN.FINANCIAL_ANALYSIS} />
          </>
        )}
      />
    )
  }

  return (
    <Layout
      renderTopComponent={renderTopComponent}
      renderBottomComponent={renderBottomComponent}
      renderStickyComponent={
        !preferenceSetting ? renderStickyComponent : undefined
      }
      isShowFooter={false}
    />
  )
}

export default FinancialAnalysis
